import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Loader2, FileText, Trash2, Briefcase, Clapperboard } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { BriefStatusBadge } from '../components/BriefQuickView';
import { PageHeader } from '../components/PageHeader';
import TreatmentsList from '../components/treatment/TreatmentsList';
import { PageToolbar } from '../components/PageToolbar';

export default function BriefsPage() {
  const { briefs, briefsLoading, fetchBriefs, createBrief, deleteBrief, clients, fetchClients } = useAppStore();
  const { hasProjectsPlan, isAdmin, isDemo, checkProjectsPlanAccess, projectsPlanChecked } = useAuthStore();
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [showTreatmentDialog, setShowTreatmentDialog] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'treatments' ? 'treatments' : 'briefs';
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ project_name: '', client_id: '' });

  useEffect(() => {
    if (!projectsPlanChecked) checkProjectsPlanAccess();
  }, [projectsPlanChecked, checkProjectsPlanAccess]);

  useEffect(() => {
    fetchBriefs();
    fetchClients();
  }, [fetchBriefs, fetchClients]);

  const handleCreate = async () => {
    if (!form.project_name.trim()) {
      toast.error('Project name is required');
      return;
    }
    setCreating(true);
    try {
      const client = clients.find((c) => c.id === form.client_id);
      const brief = await createBrief({
        project_name: form.project_name.trim(),
        client_id: form.client_id || null,
        client: client?.business_name || null,
      });
      toast.success('Brief created');
      setShowDialog(false);
      setForm({ project_name: '', client_id: '' });
      navigate(`/briefs/${brief.id}`);
    } catch (e) {
      toast.error('Failed to create brief');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, brief) => {
    e.stopPropagation();
    if (!window.confirm(`Delete brief "${brief.project_name}"? This cannot be undone.`)) return;
    try {
      await deleteBrief(brief.id);
      toast.success('Brief deleted');
    } catch (err) {
      toast.error('Failed to delete brief');
    }
  };

  if (!hasProjectsPlan && !isAdmin && !isDemo) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto text-center py-20">
          <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
          <h1 className="section-title mb-2">Briefs</h1>
          <p className="text-muted-foreground">Briefs are available on the Filmmaker plan.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="briefs-page">
        <PageHeader title="Briefs" />

        {/* Tabs on the left, the tab's action on the right, as on Jobs and Equipment */}
        <PageToolbar className="sm:flex-row sm:items-center sm:justify-between">
          <Tabs value={tab} onValueChange={(v) => setSearchParams(v === 'briefs' ? {} : { tab: v })} className="w-full sm:w-auto">
            <TabsList>
              <TabsTrigger value="briefs" className="flex items-center gap-2" data-testid="briefs-tab-briefs">
                <FileText className="w-4 h-4" /> Briefs
              </TabsTrigger>
              <TabsTrigger value="treatments" className="flex items-center gap-2" data-testid="briefs-tab-treatments">
                <Clapperboard className="w-4 h-4" /> Shot lists
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {tab === 'treatments' ? (
            <Button onClick={() => setShowTreatmentDialog(true)} data-testid="new-treatment-btn">
              <Plus className="w-4 h-4" /> New shot list
            </Button>
          ) : (
            <Button onClick={() => setShowDialog(true)} data-testid="new-brief-btn">
              <Plus className="w-4 h-4" /> New brief
            </Button>
          )}
        </PageToolbar>

        {tab === 'treatments' ? (
          <TreatmentsList dialogOpen={showTreatmentDialog} onDialogOpenChange={setShowTreatmentDialog} />
        ) : (
        <Card className="rounded-cards overflow-hidden">
          <CardContent className="p-0">
            {briefsLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : briefs.length === 0 ? (
              <div className="text-center py-20">
                <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No briefs yet. Create your first brief to get started.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Project</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Deliverables</TableHead>
                    <TableHead>Job</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {briefs.map((brief) => {
                    return (
                      <TableRow
                        key={brief.id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/briefs/${brief.id}`)}
                        data-testid={`brief-row-${brief.id}`}
                      >
                        <TableCell className="font-medium">{brief.project_name}</TableCell>
                        <TableCell className="text-muted-foreground">{brief.client || '—'}</TableCell>
                        <TableCell>
                          <BriefStatusBadge status={brief.status} />
                        </TableCell>
                        <TableCell className="text-center">{brief.deliverables?.length || 0}</TableCell>
                        <TableCell>
                          {brief.job_id ? (
                            <span className="inline-flex items-center gap-1 text-xs text-primary">
                              <Briefcase className="w-3 h-3" /> Linked
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={(e) => handleDelete(e, brief)}
                            data-testid={`delete-brief-${brief.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New brief</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="brief-project-name">Project name *</Label>
              <Input
                id="brief-project-name"
                value={form.project_name}
                onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                placeholder="e.g. Summer Campaign 2026"
                data-testid="brief-project-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="brief-client-select">
                  <SelectValue placeholder="Select a client (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.business_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="create-brief-submit">
              {creating && <Loader2 className="w-4 h-4 animate-spin" />}
              Create brief
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
